import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, FieldAPI, FormAPI, InputAPI, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnChange, OnClick, OnMount, OnReadSuccess, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, ClickEvent, MountEvent, ReadEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FragmentNames } from '../../../../codegen';
import { GlycemicAdministrationAliases as Aliases } from '../../../../codegen/constants/aliases/glycemic-administration-aliases';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import { BarcodeUtils } from '../../../../utils/barcode-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters } from '../../../../utils/parameters';
import { TimeUtils } from '../../../../utils/time-utils';
import BaseController from '../../../BaseController';
import { AdminModalsFractionalDoseService } from '../../services/admin-modals-fractional-dose-service';
import { AdminModalsService } from '../../services/admin-modals-service';
import MedicationService, { CreateEventMedicationConferenceRequest, CreatePrescriptionScheduleChangeRequest, CreatePrescriptionScheduleChangeResponse } from '../../services/medication-service';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../services/timeline-service';
import GlycemicIntensiveService from './glycemic-intensive-service';
import GlycemicQuestionService, { Answer } from './glycemic-question-service';
import { ApplicationSite, ApplicationSiteRequest, GlycemicControl, GlycemicMedicationConference, GlycemicMedicationResponse, GlycemicQuestion, GlycemicRegistrationLevel, IntensiveGlycemicMeasurementResponse, ResponseBodyProps, Variation } from './glycemic-types';
import { GlycemicAction, GlycemicStep, GlycemicTypes, VARIATIONS } from './glycemic-utils';
import BarcodeService from '../../../../services/barcode-service';
import { ConsistAdministrationMarginInfo } from '../recommendation-orders/consist-administration-margin-info';

@Controller({ fragment: FragmentNames.GLYCEMIC_ADMINISTRATION })
export default class GlycemicAdministrationController extends BaseController {

  @Inject(Aliases.GLYCEMIC_TITLE_FIELD)
  private titleFieldApi!: FieldAPI;

  @Inject(Aliases.GLYCEMIC_SCHEDULE_DATE_FIELD)
  private scheduleDateFieldApi!: FieldAPI;

  @Inject(Aliases.GLOBAL_ACTIONS_PANEL)
  private globalActionsPanelApi!: PanelAPI;

  @Inject(Aliases.NEXT_BUTTON)
  private nextButtonApi!: ButtonAPI;

  @Inject(Aliases.CONTENT_FORM)
  private contentFormApi!: FormAPI;

  @Inject(Aliases.VARIATION_AUTOCOMPLETE)
  private variationAutocompleteApi!: AutocompleteAPI;

  @Inject(Aliases.GLYCEMIC_LEVEL_INPUT)
  private glycemicLevelInputApi!: InputAPI;

  @Inject(Aliases.JUSTIFICATION_FIELD)
  private justificationFieldApi!: FieldAPI;

  @Inject(Aliases.NOTES_FIELD)
  private notesFieldApi!: FieldAPI;

  @Inject(Aliases.LOADING_PANEL)
  private loadingPanelApi!: PanelAPI;

  @Inject(Aliases.BODY_MEDICATION_PANEL)
  private bodyMedicationPanelApi!: PanelAPI;

  @Inject(Aliases.MEDICATION_DECK)
  private medicationDeckApi!: DeckAPI;

  @Inject(Aliases.BODY_BARCODE_PANEL)
  private bodyBarcodePanelApi!: PanelAPI;

  @Inject(Aliases.GLYCEMIC_BARCODE)
  private glycemicBarcodeApi!: BarcodeAPI;

  @Inject(Aliases.FIELD_APPLICATION_SITES)
  private applicationSitesFieldApi!: FieldAPI;

  @Inject(Aliases.SUGGESTION_FIELD)
  private suggestionFieldApi!: FieldAPI;

  @Inject(Aliases.ACCORDION_CONTENT_PANEL)
  private accordionContentPanelApi!: PanelAPI;

  @OnMount(Aliases.GLYCEMIC_ADMINISTRATION_LAYOUT)
  onMountLayout(): void {
    const { itemDescription, scheduleDate } = this.context.getBulkData() as GlycemicControl;
    this.titleFieldApi.setData({ title: itemDescription });
    this.scheduleDateFieldApi.setData({ scheduleDate: moment(scheduleDate).format('L LTS') });

    this.context.set({
      isValidBarcodeValidations: false,
      onFormSubmit: this.handleApplicationSite,
      step: GlycemicStep.QUESTIONS,
    });
  }

  @OnMount(Aliases.CONTENT_FORM)
  async handleMountContentForm(event: MountEvent): Promise<void> {
    const formApi = event.target as FormAPI
    const { isConventional } = this.context.getBulkData() as GlycemicControl;
    await this.getRegistrationLevels();
    if (isConventional) {
      const { glycemicQuantity, variation, suggestionDescription } = this.context.get('glycemicRegistrationLevel') as GlycemicRegistrationLevel
      if (!glycemicQuantity) {
        return;
      }

      formApi.setRecord({
        ...formApi.getRecord(),
        glycemicLevel: glycemicQuantity,
        notes: suggestionDescription,
        variation: VARIATIONS.find(item => item.id === variation),
      });
      this.variationAutocompleteApi.setEnabled(false);
      suggestionDescription && this.notesFieldApi.setVisible(true);
      this.preparePerformContext()
      return
    }

    const intensiveRegistrationLevels = this.context.get('intensiveRegistrationLevels') as IntensiveGlycemicMeasurementResponse
    if (intensiveRegistrationLevels && intensiveRegistrationLevels.registered) {
      this.suggestionFieldApi.setVisible(true);
      formApi.setRecord({
        ...formApi.getRecord(),
        glycemicLevel: intensiveRegistrationLevels.amount,
        suggestion: intensiveRegistrationLevels.suggestion || 'text.glycemic-level-non-action'
      });
      this.preparePerformContext();
    }
  }

  private preparePerformContext(): void {
    this.glycemicLevelInputApi.setEnabled(false);
    this.context.set({ alreadyRegisteredGlycemic: true });
    this.handleQuestionButtonNextClick();
    this.contentFormApi.validate();
  }

  @OnChange(Aliases.VARIATION_AUTOCOMPLETE)
  handleChangeVariationAutoComplete(): void {
    const { formContentValid } = this.context.getBulkData();
    this.nextButtonApi.setEnabled(!!formContentValid);
  }

  @OnChange(Aliases.GLYCEMIC_LEVEL_INPUT)
  handleChangeGlycemicLevelInput(event: ChangeEvent<InputAPI>): void {
    const { step } = this.context.getBulkData();
    const value = String(event.value)
    this.nextButtonApi.setEnabled(step === GlycemicStep.QUESTIONS && value.length !== 0)
  }

  @OnMount(Aliases.MEDICATION_DECK)
  handleMountMedicationDeck(event: MountEvent): void {
    const medicationDeckAPI = event.target as DeckAPI
    const conferenceItems = this.context.get('conferenceItems')

    if (conferenceItems) {
      medicationDeckAPI.setData(conferenceItems)
    }
  }

  @OnMount(Aliases.VARIATION_AUTOCOMPLETE)
  onMountVariationAutocomplete(event: MountEvent): void {
    const autocompleteApi = event.target as AutocompleteAPI;
    autocompleteApi.setData(VARIATIONS);
  }

  @OnMount(Aliases.FIELD_APPLICATION_SITES)
  handleApplicationSitesFieldMount(): void {
    this.showApplicationSitesField();
  }

  private async showApplicationSitesField(): Promise<void> {
    this.applicationSitesFieldApi.setVisible(false);
    const itemsToConference = this.context.get('itemsToConference') as GlycemicMedicationConference[];
    const isValidBarcodeValidations = this.context.get('isValidBarcodeValidations') as boolean;

    if (!itemsToConference || itemsToConference.length === 0) {
      return;
    }

    const hasApplicSites = await this.hasApplicationSites();

    if (hasApplicSites && isValidBarcodeValidations) {
        this.applicationSitesFieldApi.setVisible(true);
    }
  }

  @OnValidate(Aliases.CONTENT_FORM)
  onValidate(event: ValidateEvent): void {
    const formContentValid = event.valid
    this.context.set({ formContentValid })
  }

  @OnMount(Aliases.EVENT_DATE_INPUT)
  onMountEventDate(event: MountEvent): void {
    const dateInputApi = event.target as InputAPI;
    const { eventDate } = this.context.getBulkData();

    eventDate && dateInputApi.setValue(eventDate as string);
    !eventDate && TimeUtils.setDateToInput(dateInputApi)
      .then(date => this.context.set({ eventDate: date }));
  }

  @OnMount(Aliases.NEXT_BUTTON)
  handleNextButtonMount(): void {
    const { step, isConventional, isValidBarcodeValidations, formContentValid, performType } = this.context.getBulkData();

    if (step === GlycemicStep.QUESTIONS) {
      this.nextButtonApi.setEnabled(!!formContentValid);
      this.nextButtonApi.setValue('text.next');
      return;
    }
    this.nextButtonApi.setEnabled(!!formContentValid && isValidBarcodeValidations as boolean);
    this.nextButtonApi.setValue(isConventional ? performType as string : 'text.realize');
  }

  @OnClick(Aliases.NEXT_BUTTON)
  handleNext(): void {
    const step = this.context.get('step') as GlycemicStep;
    if (step === GlycemicStep.QUESTIONS) {
      this.handleQuestionButtonNextClick();
    } else if (step === GlycemicStep.BARCODE_VALIDATION) {
      this.handlePerformClick();
    }
  }

  @OnClick(Aliases.GLYCEMIC_MEDICATION_DETAILS_CARD)
  handleResetCardRetails(event: ClickEvent): void {
    const scheduleSequence = event.value.scheduleSequence || event.value.barcode;

    BarcodeUtils.dialogModalConfirm(scheduleSequence, this.resetOneMedicationsCard);
  }

  @OnMount(Aliases.GLYCEMIC_BARCODE)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess(Aliases.GLYCEMIC_BARCODE)
  onReadSuccess(event: ReadEvent<BarcodeData>): void {
    const readBarcode = event.item.value.trim();
    this.readOneMedicationsCard(readBarcode)
      .then(responseBody => {
        const { totalRead, total, message, messageType, informDose } = responseBody as unknown as ResponseBodyProps;

        if (informDose) {
          this.handleModalAdminScheduleFractionalDose(readBarcode);
          return;
        }

        this.searchAllMedicationsPendding();

        if (!message) {
          MedicationService.showToast(MessageService.translate('text-medication-read-with-success'), ToastLevel.SUCCESS);
        } else {
          MedicationService.showToast(message, this.toastType(messageType));
        }

        if (total !== 0 && totalRead === total) {
          this.context.set({ isValidBarcodeValidations: true });
          this.contentFormApi.validate();
        }

        this.showApplicationSitesField();
      })
      .catch((error) => {
        error.body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
      })
      .finally(() => this.glycemicBarcodeApi.close());
  }

  private async getRegistrationLevels(): Promise<void> {
    const { isConventional } = this.context.getBulkData() as GlycemicControl;
    let values: GlycemicRegistrationLevel | IntensiveGlycemicMeasurementResponse | null;
    if (isConventional) {
      values = await this.findGlycemicRegistrationLevels();
      if (values) {
        this.context.set({ glycemicRegistrationLevel: values });
      }
    } else {
      values = await this.findIntensiveRegistrationLevels();
      if (values) {
        this.context.set({ intensiveRegistrationLevels: values });
      }
    }
    this.accordionContentPanelApi.setVisible(true);
  }

  private async findIntensiveRegistrationLevels(): Promise<IntensiveGlycemicMeasurementResponse | null> {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    return GlycemicIntensiveService.getMeasurementPerformed(scheduleSequenceNumber as number)
      .then(data => {
        if (data) {
          return data;
        }
        return null;
      }).catch((error) => {
        MedicationService.showToast(error.message, ToastLevel.ERROR);
        return null;
      });
  }

  private async findGlycemicRegistrationLevels(): Promise<GlycemicRegistrationLevel | null> {
    const { scheduleSequenceNumber } = this.context.getBulkData() as GlycemicControl;
    return HttpService.get<GlycemicRegistrationLevel>(Endpoint.EMAR_GET_GLYCEMIC_REGISTRATION_LEVEL, { scheduleSequenceNumber })
      .then(response => {
        if (response?.body) {
          return response.body;
        }
        return null;
      }).catch((error) => {
        MedicationService.showToast(error.message, ToastLevel.ERROR);
        return null;
      });
  }

  private async hasApplicationSites(): Promise<boolean> {
    const { glycemicProtocolSequence } = this.context.getBulkData() as GlycemicControl;
    return HttpService.get<ApplicationSite[]>(Endpoint.EMAR_GET_GLYCEMIC_APPLICATION_SITES, { glycemicProtocolSequence })
      .then(response => {
        if (!response.body || response.body.length <= 0) {
          return false;
        }
        return true;
      }).catch((error) => {
        MedicationService.showToast(error.message, ToastLevel.ERROR);
        return false;
      });
  }

  private handlePerformClick(): void {
    this.handlePerform();
  }

  private handlePerform(): void {
    this.toggleLoading();
    this.context.set({ redirect: true });

    this.createEventMedicationConference()
      .then(() => this.performIntensiveConfirmation())
      .then(() => this.performConsist())
      .then(() => this.performGlycemic())
      .then(() => this.performAssociatedItems())
      .then(() => this.registerApplicationSite())
      .then(() => {
        MedicationService.showToast(MessageService.translate('text.perform-with-success'), ToastLevel.SUCCESS);
      })
      .catch(rejected => {
        if (rejected.message) {
          MedicationService.showToast(rejected.message, ToastLevel.WARNING);
        }

        if (rejected.body && rejected.body.errors) {
          rejected.body.errors.map((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
      })
      .finally(() => {
        this.toggleLoading();
        this.redirectToTimeline();
      });
  }

  private registerApplicationSite(): Promise<unknown> {
    const { applicationSiteCode, scheduleSequenceNumber, prescriptionNumber } = this.context.getBulkData() as GlycemicControl;

    const body: ApplicationSiteRequest = {
      applicationSiteCode: applicationSiteCode as string,
      prescriptionNumber,
      scheduleSequenceNumber,
    };

    return HttpService.post(Endpoint.EMAR_POST_GLYCEMIC_REGISTER_APPLICATION_SITE, undefined, { body });
  }

  private performIntensiveConfirmation(): Promise<void> {
    const { scheduleSequenceNumber } = this.context.getBulkData() as GlycemicControl;
    return GlycemicIntensiveService.confirmIntensivePerformation(scheduleSequenceNumber);
  }

  private performConsist(): Promise<string | void> {
    const { scheduleSequenceNumber } = this.context.getBulkData() as GlycemicControl;
    const { eventDate, justification } = this.contentFormApi.getRecord();

    return ConsistAdministrationMarginInfo.execute({
      administrationDate: eventDate as string,
      itemType: PrescriptionType.GLYCEMIC_CONTROL,
      justification: justification as string | undefined,
      scheduleSequenceNumber: scheduleSequenceNumber
    }).then(response => {
      if (response.responseDescription) {
        this.context.set({ redirect: false });
        MedicationService.showToast(response.responseDescription, ToastLevel.WARNING);
      }
    });
  }

  createEventMedicationConference(): Promise<unknown> {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const formValues = this.contentFormApi.getRecord();
    const justification = formValues.justification;
    const {
      glycemicProtocolSequence,
      internalProcedureSequence,
      prescriptionNumber,
      scheduleSequenceNumber
    } = this.context.getBulkData() as GlycemicControl;

    const request: CreateEventMedicationConferenceRequest = {
      changeStatus: 58,
      encounterNumber: Number(encounterId),
      glucoseProteinSequence: glycemicProtocolSequence,
      internalProcessSequence: internalProcedureSequence,
      changeDate: moment().format(ISO_FULL_DATE_FORMAT),
      itemCode: Number(timelineMedication.itemCode),
      itemSequence: Number(timelineMedication.itemSequence) || 1,
      itemType: timelineMedication.itemType,
      justifyDescription: justification ? String(justification) : '',
      pdoPrn: timelineMedication.pdoPrn ? 'S' : 'N',
      prescriptionNumner: prescriptionNumber,
      scheduleSequence: scheduleSequenceNumber,
    };

    return MedicationService.createEventMedicationConference(request);
  }

  private performGlycemic(): Promise<CreatePrescriptionScheduleChangeResponse> {
    const adepFunctionName = 'ADEP#1113';
    const data = this.context.getBulkData() as GlycemicControl;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { itemType, groupingNumber, pdoPrn } = TimelineContext.get();
    const { justification, eventDate } = this.contentFormApi.getRecord();
    const param22 = FoundationUtils.findModuleParam(adepFunctionName, ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY) as number;

    const performObj: CreatePrescriptionScheduleChangeRequest = {
      administrationRouteType: '',
      changeType: 3,
      componentsDescription: '',
      encounterNumber: Number(encounterId),
      eventDate: moment(eventDate as string).format(ISO_FULL_DATE_FORMAT),
      functionCode: 9129,
      glucoseProteinSequence: data.glycemicProtocolSequence,
      groupingNumber,
      intervalCode: data.intervalCode,
      isGenerateAll: 'N',
      isPalm: 'S',
      itemCode: String(data.procedureCode),
      itemDescription: data.itemDescription,
      itemObservationDescription: '',
      itemQuantity: data.doseQuantity,
      itemSequence: data.sequenceProcedure,
      itemType,
      justifyDescription: justification ? String(justification) : '',
      messageDescriptionResponse: '',
      observationDescription: '',
      pdoPrn: pdoPrn ? 'S' : 'N',
      prescriptionHourQuantity: param22,
      prescriptionNumber: data.prescriptionNumber,
      prescriptionsNumber: String(data.prescriptionNumber),
      procedureInternalSequenceNumber: data.internalProcedureSequence,
      scheduleSequence: data.scheduleSequenceNumber,
      suspendPrescribedMaterial: '',
      unitMeasureCode: '',
      uoMCodeQuantityAdministered: '',
    };

    return MedicationService.createPrescriptionScheduleChange(performObj);
  }

  private redirectToTimeline(): void {
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const { redirect } = this.context.getBulkData();

    if (!redirect) {
      return;
    }

    AdminModalsService.redirectToTimeline(FragmentNames.GLYCEMIC_ADMINISTRATION);
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
  }

  private performAssociatedItems(): Promise<HttpResponse<unknown> | null> {
    const performType = this.context.get('performType') as GlycemicAction;

    if (performType !== GlycemicAction.BY_PROCESS) {
      return Promise.resolve(null);
    }

    const { scheduleSequenceNumber, prescriptionNumber, procedureCode, sequenceProcedure, internalProcedureSequence } = this.context.getBulkData() as GlycemicControl;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { eventDate } = this.contentFormApi.getRecord();

    const performItems = {
      encounterNumber: encounterId,
      eventDate,
      internalProcedureSequence,
      prescriptionNumber,
      procedureCode,
      scheduleSequenceNumber,
      sequenceProcedure,
    } as Record<string, Value>;

    return HttpService.post(Endpoint.EMAR_POST_GLYCEMIC_PERFORM_ASSOCIATED_ITEMS, undefined, { body: performItems });
  }

  private handleQuestionButtonNextClick(): void {
    const {
      scheduleSequenceNumber,
      alreadyRegisteredGlycemic = false,
      isConventional = false
    } = this.context.getBulkData() as GlycemicControl;

    const { glycemicLevel } = this.contentFormApi.getRecord();

    if (alreadyRegisteredGlycemic) {
      const glycemicRegisterObj: GlycemicQuestion = {
        isAnswer: '',
        isPreviousAnswer: '',
        questionDescription: '',
        questionSequence: 0
      };

      this.register('', glycemicRegisterObj);
      return;
    }

    if (!isConventional) {
      this.calculateIntensiveLevels(Number(glycemicLevel));
      return;
    }

    this.toggleLoading();
    HttpService.get<GlycemicQuestion[]>(Endpoint.EMAR_GET_GLYCEMIC_QUESTIONNARIES, { glycemicLevel, scheduleSequenceNumber })
      .then(response => {
        this.toggleLoading();

        if (!response.body) {
          return;
        }

        new GlycemicQuestionService(response.body, this.register.bind(this))
          .start();
      })
      .catch(() => {
        this.toggleLoading();
        this.showToast(MessageService.translate('error.glycemic-levels-not-found'), ToastLevel.ERROR);
      });
  }

  private searchAllMedicationsPendding = (): Promise<Record<string, Value>[]> => {
    return this.fetchAllMedicationPendding().then((res) => {
      const responseBody = res.body as Record<string, Value>[];
      const noReading = responseBody.filter(((item) => item.read === false));

      this.medicationDeckApi.setDone(true);
      this.medicationDeckApi.setLoading(true);

      if (noReading.length === 0) {
        this.context.set({ isValidBarcodeValidations: true });
        this.contentFormApi.validate();
        this.nextButtonApi.setEnabled(true);
      }

      if (responseBody?.length > 0) {
        this.medicationDeckApi.setData(this.normalizeConferenceItems(
          responseBody as GlycemicMedicationConference[],
          this.context.getBulkData() as unknown as GlycemicMedicationResponse
        ));
        this.medicationDeckApi.setLoading(false);
      }

      return responseBody;
    }).catch((error) => {
      this.showToast(error.message, ToastLevel.ERROR)
      return Promise.reject()
    });
  }

  private fetchAllMedicationPendding(): Promise<HttpResponse> {
    const emarTimelineEvent = TimelineContext.get();
    const performType = this.context.get('performType') as GlycemicAction;
    const { prescriptionNumber, sequenceProcedure, processes } = this.context.getBulkData() as GlycemicControl;

    if (performType === GlycemicAction.BY_PROCESS) {
      const processSequence = processes?.find(process => process.processSequence)?.processSequence;

      return HttpService.get(Endpoint.EMAR_GET_ALL_PROCESS, {
        labelStatus: null,
        processSequence,
      });
    }

    return HttpService.get(Endpoint.EMAR_GET_MEDICATIONS_PENDING_PRESCRIPTION_TYPE, {
      prescriptionNumber,
      prescriptionType: PrescriptionType.GLYCEMIC_CONTROL,
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: sequenceProcedure,
    });
  }

  private calculateIntensiveLevels(glycemicLevel: number): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { scheduleSequenceNumber } = this.context.getBulkData() as GlycemicControl;
    GlycemicIntensiveService.calculateIntensiveLevels(Number(encounterId), scheduleSequenceNumber, glycemicLevel)
      .then(data => {
        this.suggestionFieldApi.setValue(data.suggestion);
        return this.register('')
      })
      .finally(() => this.suggestionFieldApi.setVisible(true))
  }

  private resetOneMedicationsCard = (barcodeValue: string): void => {
    this.getFetchServiceToResetOneMedicationCard(barcodeValue)
      .then((resp) => {
        const response = resp.body as ResponseBodyProps;
        const message = response.message;

        MedicationService.showToast(String(message), ToastLevel.SUCCESS);

        this.searchAllMedicationsPendding().then((responseBody) => {
          const responseSchedule = responseBody as unknown as ResponseBodyProps[];
          const withoutReading = responseSchedule.filter(item => item.read === false);

          if (withoutReading.length > 0) {
            this.glycemicBarcodeApi.setEnabled(true);
            this.context.set({ isValidBarcodeValidations: false });
          }

          this.showApplicationSitesField();

          this.contentFormApi.validate();
          this.nextButtonApi.setEnabled(false);
        }).catch((error) => {
          MedicationService.showToast(error.message, ToastLevel.ERROR);
        });
      }).catch((error) => {
        const errorMessage = error.body.errors[0];
        MedicationService.showToast(errorMessage, ToastLevel.ERROR);
      });
  }

  private getFetchServiceToResetOneMedicationCard(barcodeValue: string): Promise<HttpResponse<ResponseBodyProps>> {
    const performType = this.context.get('performType') as GlycemicAction;
    const emarTimelineEvent = TimelineContext.get();
    const { prescriptionNumber, sequenceProcedure, processes } = this.context.getBulkData() as GlycemicControl;
    const processSequence = processes?.find(process => process.processSequence)?.processSequence;

    if (performType === GlycemicAction.BY_PROCESS) {
      const [, code] = barcodeValue.split(',');
      return HttpService.put<ResponseBodyProps>(Endpoint.EMAR_RESET_ONE_PROCESS, {
        barcode: code,
        processSequence,
      });
    }

    return HttpService.put<ResponseBodyProps>(Endpoint.EMAR_RESET_ONE_MEDICATIONS_CARD, {
      prescriptionNumber,
      scheduleCurrentSequence: Number(barcodeValue),
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: sequenceProcedure,
    });
  }

  private handleBarcodeClose = (): void => {
    this.glycemicBarcodeApi.close();
  }

  private handleModalAdminScheduleFractionalDose(currentBarcode: string): void {
    const { scheduleDate, prescriptionNumber, sequenceProcedure } = this.context.getBulkData() as GlycemicControl;

    const paramsAdmFractionDose = {
      barcodeClose: this.handleBarcodeClose,
      currentBarcode,
      medicationOrSolution: 'medication',
      prescriptionNumber: prescriptionNumber,
      prescriptionType: PrescriptionType.GLYCEMIC_CONTROL,
      scheduleDate: scheduleDate,
      scheduleSequenceNumber: Number(sequenceProcedure),
      updatedCards: this.searchAllMedicationsPendding,
    };

    AdminModalsFractionalDoseService.modalAdminScheduleFractionalDose(paramsAdmFractionDose);
  }

  private readOneMedicationsCard(barcodeRead: string): Promise<void | Record<string, Value>[]> {
    const emarTimelineEvent = TimelineContext.get();
    const { sequenceProcedure, performType } = this.context.getBulkData() as GlycemicControl;

    if (performType === GlycemicAction.BY_PROCESS) {
      return this.readOneMedicationsCardProcess(barcodeRead);
    }

    const body = {
      barcode: barcodeRead,
      prescriptionNumber: emarTimelineEvent.prescriptionNumber,
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: Number(sequenceProcedure),
    };

    return HttpService.post<Record<string, Value>[]>(Endpoint.EMAR_POST_GLYCEMIC_CONFIRM_BARCODE, {}, { body }
    ).then(response => response.body);
  }

  private readOneMedicationsCardProcess(barcode: string): Promise<void | Record<string, Value>[]> {
    const { processes } = this.context.getBulkData() as GlycemicControl;
    const processSequence = processes?.find(item => item.processSequence)?.processSequence;

    const body = {
      barcode,
      processSequence
    };

    return HttpService.post<Record<string, Value>[]>(Endpoint.EMAR_CONFIRM_BARCODE, {}, { body })
      .then(response => response.body);
  }

  private resetAllMedicationsCard(): Promise<void | Record<string, Value>[]> {
    const emarTimelineEvent = TimelineContext.get();
    const { scheduleSequenceNumber, processes } = this.context.getBulkData() as GlycemicControl;
    const processSequence = processes?.find(process => process.processSequence)?.processSequence;
    return HttpService.put<Record<string, Value>[]>(this.getEndpointToResetAllMedicationsCard(), {
      prescriptionNumber: emarTimelineEvent.prescriptionNumber,
      processSequence,
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: scheduleSequenceNumber,
    }, {}).then(() => {
      return Promise.resolve();
    }).catch(error => {
      this.showToast(error.message, ToastLevel.ERROR)
      return Promise.reject();
    });
  }

  private getEndpointToResetAllMedicationsCard(): string {
    const performType = this.context.get('performType');
    if (performType === GlycemicAction.BY_PROCESS) {
      return Endpoint.EMAR_ALL_RESET_PROCESS;
    }
    return Endpoint.EMAR_RESET_ALL_MEDICATIONS_CARD;
  }

  private register(answers: string, lastQuestObj?: GlycemicQuestion, lastAnswar?: Answer): void {
    this.toggleLoading();
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { scheduleSequenceNumber, sequenceProcedure, isConventional } = this.context.getBulkData() as GlycemicControl;
    const { glycemicLevel, eventDate, variation } = this.contentFormApi.getRecord();
    const variationValue = variation ? (variation as Variation).id : 'N';
    const performType = this.getPerformType();
    const glycemicTypes = isConventional ? GlycemicTypes.CONVENTIONAL : GlycemicTypes.INTENSIVE;

    const body = {
      encounterNumber: encounterId,
      eventDate: moment(eventDate as string).format(ISO_FULL_DATE_FORMAT),
      glycemicLevel,
      glycemicTypes,
      lastQuestionResponse: lastAnswar,
      performType: performType,
      questionParameter: answers,
      questionSequence: lastQuestObj?.questionSequence,
      scheduleSequenceNumber,
      sequenceProcedure,
      variation: variationValue,
    } as Record<string, Value>;

    this.resetAllMedicationsCard()
      .then(() => {
        HttpService.post<GlycemicMedicationResponse>(Endpoint.EMAR_POST_GLYCEMIC_REGISTER_LEVELS, undefined, { body })
          .then(response => response.body && this.prepareAdministration(response.body))
          .catch((error) => {

            this.toggleLoading();
            if (error.body?.errors.length > 0) {
              error.body.errors.forEach((message: string) => this.showToast(message, ToastLevel.ERROR));
            }
          });
      });
  }

  private getPerformType(): string {
    const performType = this.context.get('performType') as GlycemicAction;
    if (GlycemicAction.BY_PROCESS === performType) {
        return 'PROCESS';
    }
    return 'HOUR';
  }

  private prepareAdministration(glycemicMedication: GlycemicMedicationResponse): void {

    this.toggleLoading();
    this.context.set({ step: GlycemicStep.BARCODE_VALIDATION, ...glycemicMedication });

    const hasConferenceItems = glycemicMedication?.itemsToConference?.length > 0;
    this.showApplicationSitesField();
    const conferenceItems = hasConferenceItems ? this.normalizeConferenceItems(glycemicMedication.itemsToConference, glycemicMedication, true) : [];

    const isAllowToShowBarcode = this.isAllowToShowBarcode(glycemicMedication);
    this.bodyBarcodePanelApi.setVisible(isAllowToShowBarcode);

    this.glycemicLevelInputApi.setEnabled(false);

    this.variationAutocompleteApi.setEnabled(false);

    glycemicMedication.orientation && this.notesFieldApi.setVisible(true);
    glycemicMedication.orientation && this.notesFieldApi.setValue(glycemicMedication.orientation);
    this.bodyMedicationPanelApi.setVisible(hasConferenceItems);
    this.justificationFieldApi.setVisible(true);

    hasConferenceItems && this.context.set({ conferenceItems });

    if (!hasConferenceItems || !this.isAllowToShowBarcode(glycemicMedication)) {
      this.context.set({ isValidBarcodeValidations: true });
    }

    this.contentFormApi.validate();
  }

  private handleApplicationSite = (applicationSite: ApplicationSite): void => {
    this.context.set({ applicationSiteCode: applicationSite.code });
  }

  private normalizeConferenceItems(conferenceItems: GlycemicMedicationConference[], glycemicMedication: GlycemicMedicationResponse, changedReadParam?: boolean): GlycemicMedicationConference[] {
    const isAllowToShowBarcode = this.isAllowToShowBarcode(glycemicMedication);
    return conferenceItems.map(item => {
      const scheduleItem = glycemicMedication.conferences.find(schedule =>
        schedule.sequenceMaterialSchedule === item.scheduleSequence || schedule.prescriptionNumber === item.prescriptionNumber);
      return {
        ...item,
        canUndo: isAllowToShowBarcode,
        read: changedReadParam ? false : item.read,
        schedules: scheduleItem && scheduleItem.schedules,
      };
    });
  }

  private isAllowToShowBarcode(glycemicMedication: GlycemicMedicationResponse): boolean {
    const performType = this.context.get('performType') as GlycemicAction;
    const generatedSupplementaryMedicine = glycemicMedication.itemsToConference !== null && glycemicMedication.itemsToConference.length > 0

    if (generatedSupplementaryMedicine
      && glycemicMedication.requestLabel
      && GlycemicAction.BY_PROCESS === performType) {
      return true;
    }

    return false;
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      id: 'gylcemic-administration',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 7000,
      type: ToastType.BUBBLE,
    })
  }

  private toggleLoading(): void {
    this.globalActionsPanelApi.setVisible(!this.globalActionsPanelApi.isVisible());
    this.loadingPanelApi.setVisible(!this.loadingPanelApi.isVisible());
  }

  private toastType(message: string): ToastLevel {
    switch (message) {
      case 'SUCCESS':
        return ToastLevel.SUCCESS;
      case 'ERROR':
        return ToastLevel.ERROR;
      default:
        return ToastLevel.SUCCESS;
    }
  }
}
